import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/solid';

import Divider from '../components/ui/Divider';
import { Complex, Field, Game, MenuItem } from '../types';
import { useComplexes } from '../hooks/useComplexes';
import { getComplexGamesWithReservations } from '../api/reservations';
import { Player } from '../types/players';
import PlayerReservationDetailsModal from '../components/modals/PlayerReservationDetailsModal';
import Button from '../components/buttons/Button';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import EventStatusBadge from '../components/ui/EventStatusBadge';
import ReservationGameCard from '../components/cards/ReservationGameCard';
import GameReservationDetailsModal from '../components/modals/GameReservationModal';
import { getToken } from '../utils/token';
import axios from 'axios';
import LoadingBall from '../components/loading/LoadingBall';
import ReservationFiltersModal from '../components/modals/ReservationFiltersModal';
import dayjs from 'dayjs';
import { getFields } from '../api/fields';

interface ComplexWithGamesAndReservations {
  complexId: number;
  games: Game[];
  hasNext: boolean;
}

const initialPageLimit = 10;

const ReservationPage = () => {
  const { complexes } = useComplexes();

  const [openFilters, setOpenFilters] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSecondDrawerOpen, setSecondDrawerOpen] = useState(false);

  const [games, setGames] = useState<ComplexWithGamesAndReservations[]>([]);
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null);
  const [pageLimit, setPageLimit] = useState(initialPageLimit);
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedComplex, setSelectedComplex] =
    useState<MenuItem<Complex> | null>(null);
  const [selectedField, setSelectedField] = useState<MenuItem<Field> | null>(
    null
  );
  const [startDate, setStartDate] = useState<Date | null>(
    dayjs().toDate()
  );
  const [endDate, setEndDate] = useState<Date | null>(
    dayjs().toDate()
  );
  const [captain, setCaptain] = useState('');
  const [phone, setPhone] = useState('');
  const [team, setTeam] = useState('');
  const [owner, setOwner] = useState('');

  const { t } = useTranslation();

  const fetchFields = useCallback(async (complexId: number) => {
    setLoadingFields(true);

    const { data, error } = await getFields(complexId, 1, 50);

    if (error) {
      console.error('Error fetching fields:', error);
      toast(error.error);
    }

    if (data) {
      setFields(data.fields);
    }

    setLoadingFields(false);
  }, []);

  const fetchGamesWithReservationsForComplex = useCallback(
    async (complexId?: number, status?: string, currentPageLimit?: number) => {
      if (!complexId) {
        return;
      }

      setLoading(true);

      const serverStatus = status === 'EXPIRED' ? 'PENDING' : status;

      const { data, error } = await getComplexGamesWithReservations(
        complexId,
        1,
        currentPageLimit || pageLimit,
        serverStatus,
        {
          fieldId: selectedField?.value?.id,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          captain,
          phone,
          team,
          user: owner,
          upcoming: selectedStatus === 'PENDING',
          passed: selectedStatus === 'EXPIRED',
        }
      );

      setLoading(false);

      if (error) {
        const { error: errMsg } = error;
        toast(errMsg, {
          toastId: 'fetch-reservations-err',
        });

        return;
      }

      if (data) {
        setGames((prev) => {
          const currentComplexIndex = prev.findIndex(
            (r) => r.complexId === complexId
          );

          if (currentComplexIndex >= 0) {
            prev[currentComplexIndex] = { complexId, ...data };
          } else {
            prev.push({
              complexId,
              ...data,
            });
          }

          return [...prev];
        });
      }
    },
    [
      pageLimit,
      selectedField?.value?.id,
      startDate,
      endDate,
      captain,
      phone,
      team,
      owner,
      selectedStatus,
    ]
  );

  const onLoadMoreClick = useCallback(async () => {
    const newPageLimit = pageLimit + initialPageLimit;
    setPageLimit(newPageLimit);

    await fetchGamesWithReservationsForComplex(
      selectedComplex?.value.id,
      selectedStatus,
      newPageLimit
    );
  }, [
    fetchGamesWithReservationsForComplex,
    selectedComplex?.value.id,
    selectedStatus,
    pageLimit,
  ]);

  const noComplexMenuOption = useMemo(
    () => ({
      label: t(COMMON_TRANSLATE_KEYS.NO_COMPLEXES),
      value: null,
      isDisabled: true,
      onClick: () => undefined,
    }),

    [t]
  );

  const complexMenuOptions = useMemo(() => {
    if (complexes.length === 0) {
      return [];
    }

    return complexes.map((c) => ({
      label: c.name,
      value: c,
      onClick: async () => {},
    }));
  }, [complexes]);

  const complexMenuItems = useMemo(() => {
    if (complexMenuOptions.length === 0) {
      return [noComplexMenuOption];
    }

    return complexMenuOptions;
  }, [complexMenuOptions, noComplexMenuOption]);

  const noFieldMenuOption = useMemo(
    () => ({
      label: t(COMMON_TRANSLATE_KEYS.COMPLEX_HAS_NO_FIELDS),
      value: null,
      isDisabled: true,
      onClick: () => undefined,
    }),
    [t]
  );

  const fieldOptions = useMemo(() => {
    return fields.map((field: Field) => {
      const option: MenuItem<Field> = {
        label: field.name,
        value: field,
        onClick: () => {},
      };
      return option;
    });
  }, [fields]);

  const fieldMenuItems: Array<MenuItem<Field | null>> = useMemo(() => {
    if (loadingFields) {
      return [
        {
          ...noFieldMenuOption,
          label: t(COMMON_TRANSLATE_KEYS.FIELDS_LOADING),
        },
      ];
    }
    if (fieldOptions.length === 0) {
      return [noFieldMenuOption];
    }

    return fieldOptions;
  }, [fieldOptions, noFieldMenuOption, loadingFields, t]);

  const handleStatusClick = useCallback(
    (status: string) => {
      if (selectedStatus.includes(status)) {
        setSelectedStatus('');
      } else {
        setSelectedStatus(status);
      }
    },
    [selectedStatus]
  );

  const handleGameClick = useCallback(
    (gameId: number, complexId: number) => {
      const game = games
        .find((r) => r.complexId === complexId)
        ?.games.find((g) => g.id === gameId);

      if (!game) {
        setCurrentGame(null);
        return;
      }

      setCurrentGame(game);
      openDrawer();
    },
    [games]
  );

  const handlePlayerClick = useCallback((player: Player) => {
    setCurrentPlayer(player);
    openSecondDrawer();
  }, []);

  const applyFilters = useCallback(
    async (
      complex: MenuItem<Complex> | null,
      field: MenuItem<Field> | null,
      startDateFilter: Date | null,
      endDateFilter: Date | null,
      captainFilter: string,
      ownerFilter: string,
      phoneFilter: string,
      teamFilter: string
    ) => {
      setOpenFilters(false);
      setSelectedComplex(complex);
      setSelectedField(field);
      setStartDate(startDateFilter);
      setEndDate(endDateFilter);
      setCaptain(captainFilter);
      setOwner(ownerFilter);
      setPhone(phoneFilter);
      setTeam(teamFilter);
    },
    []
  );

  const renderReservationCards = useCallback(() => {
    if (!selectedComplex) {
      return (
        <h2 className="text-textGray text-xl">
          {t(COMMON_TRANSLATE_KEYS.COMPLEX_SELECT)}
        </h2>
      );
    }

    const currentComplexIndex = games.findIndex(
      (r) => r.complexId === selectedComplex.value.id
    );

    if (currentComplexIndex >= 0) {
      const pendingGames = games[currentComplexIndex].games.filter(
        (g) => g.status === 'PENDING' && !g.isExpired
      );
      const pendingGamesIds = pendingGames.map((g) => g.id);

      const otherStatusGames = games[currentComplexIndex].games.filter(
        (g) => !pendingGamesIds.includes(g.id)
      );

      const pendingReservationsCards = pendingGames.map((g) => (
        <ReservationGameCard
          key={g.id}
          fetchGames={() =>
            fetchGamesWithReservationsForComplex(selectedComplex.value.id)
          }
          field={g.field}
          game={g}
          onClick={() => handleGameClick(g.id, selectedComplex.value.id)}
        />
      ));

      const otherStatusReservationsCards = otherStatusGames.map((g) => (
        <ReservationGameCard
          key={g.id}
          fetchGames={() =>
            fetchGamesWithReservationsForComplex(selectedComplex.value.id)
          }
          field={g.field}
          game={g}
          onClick={() => handleGameClick(g.id, selectedComplex.value.id)}
        />
      ));

      const reservationCardContainerClassName =
        'grid grid-cols-1 lg:grid-cols-2 gap-5 text-white';

      return (
        <div className="flex flex-col my-5 gap-4">
          <div className={reservationCardContainerClassName}>
            {pendingReservationsCards}
          </div>

          {selectedStatus.length === 0 && <Divider />}

          <div className={reservationCardContainerClassName}>
            {otherStatusReservationsCards}
          </div>

          {games[currentComplexIndex]?.hasNext && (
            <Button onClick={onLoadMoreClick} isDisabled={loading}>
              {t(COMMON_TRANSLATE_KEYS.LOAD_MORE)}
            </Button>
          )}
        </div>
      );
    }
  }, [
    selectedComplex,
    t,
    games,
    selectedStatus,
    onLoadMoreClick,
    loading,
    fetchGamesWithReservationsForComplex,
    handleGameClick,
  ]);

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => {
    setSecondDrawerOpen(false);
    setIsDrawerOpen(false);
  };

  const openSecondDrawer = () => setSecondDrawerOpen(true);
  const closeSecondDrawer = () => setSecondDrawerOpen(false);

  useEffect(() => {
    if (complexMenuOptions.length > 0 && selectedComplex === null) {
      setSelectedComplex(complexMenuOptions[0]);
    }
  }, [complexMenuOptions, selectedComplex]);

  useEffect(() => {
    if (selectedComplex) {
      fetchFields(selectedComplex.value.id);
    }
  }, [selectedComplex, fetchFields]);

  useEffect(() => {
    if (selectedComplex) {
      fetchGamesWithReservationsForComplex(
        selectedComplex.value.id,
        selectedStatus
      );
    }
  }, [fetchGamesWithReservationsForComplex, selectedComplex, selectedStatus]);

  return (
    <>
      <div className="mx-[30px] mt-8">
        <div className="flex md:flex-row flex-col text-mainWhite justify-between gap-10 mb-5">
          <div className="flex flex-row items-center">
            <Button
              IconAfter={AdjustmentsHorizontalIcon}
              border="border"
              borderColor="border-mainBlue"
              onClick={() => setOpenFilters(true)}
            >
              {`${t(COMMON_TRANSLATE_KEYS.FILTERS)}`}
            </Button>
          </div>
        </div>
        {selectedComplex && (
          <div className="flex flex-wrap w-full py-3 gap-5 justify-center items-center">
            <button onClick={() => handleStatusClick('PENDING')}>
              <EventStatusBadge
                status="PENDING"
                style={{
                  margin: 0,
                  borderColor:
                    selectedStatus === 'PENDING' ? '#42CBE5' : 'gray',
                  borderWidth: selectedStatus === 'PENDING' ? 4 : 1,
                  borderRadius: 99999,
                  padding: 10,
                }}
                dontShrink
              />
            </button>
            <button onClick={() => handleStatusClick('DECLINED')}>
              <EventStatusBadge
                status="DECLINED"
                style={{
                  margin: 0,
                  borderColor:
                    selectedStatus === 'DECLINED' ? '#42CBE5' : 'gray',
                  borderWidth: selectedStatus === 'DECLINED' ? 4 : 1,
                  borderRadius: 99999,
                  padding: 10,
                }}
                dontShrink
              />
            </button>
            <button onClick={() => handleStatusClick('CONFIRMED')}>
              <EventStatusBadge
                status="CONFIRMED"
                style={{
                  margin: 0,
                  borderColor:
                    selectedStatus === 'CONFIRMED' ? '#42CBE5' : 'gray',
                  borderWidth: selectedStatus === 'CONFIRMED' ? 4 : 1,
                  borderRadius: 99999,
                  padding: 10,
                }}
                confirmedStatusKey={t(COMMON_TRANSLATE_KEYS.TAKEN)}
                className="text-dividerGray"
                dontShrink
              />
            </button>
            <button onClick={() => handleStatusClick('CANCELED')}>
              <EventStatusBadge
                status="CANCELED"
                style={{
                  margin: 0,
                  borderColor:
                    selectedStatus === 'CANCELED' ? '#42CBE5' : 'gray',
                  borderWidth: selectedStatus === 'CANCELED' ? 4 : 1,
                  borderRadius: 99999,
                  padding: 10,
                }}
                dontShrink
              />
            </button>
            <button onClick={() => handleStatusClick('EXPIRED')}>
              <EventStatusBadge
                status="PENDING"
                style={{
                  margin: 0,
                  borderColor:
                    selectedStatus === 'EXPIRED' ? '#42CBE5' : 'gray',
                  borderWidth: selectedStatus === 'EXPIRED' ? 4 : 1,
                  borderRadius: 99999,
                  padding: 10,
                }}
                isExpired
                dontShrink
              />
            </button>
          </div>
        )}
        {renderReservationCards()}
        {loading ? <LoadingBall className="w-full h-[50%]" /> : <></>}
      </div>

      <ReservationFiltersModal
        openFilters={openFilters}
        closeFilters={() => setOpenFilters(false)}
        complexMenuItems={complexMenuItems}
        fieldMenuItems={fieldMenuItems}
        loadingFields={loadingFields}
        selectedComplex={selectedComplex}
        selectedField={selectedField}
        selectedStartDate={startDate}
        selectedEndDate={endDate}
        initialCaptain={captain}
        initialOwner={owner}
        initialPhone={phone}
        initialTeam={team}
        onApplyFilters={applyFilters}
        fetchFields={fetchFields}
      />

      <div>
        <div className="relative z-30">
          <GameReservationDetailsModal
            game={currentGame}
            closeDrawer={closeDrawer}
            isOpen={isDrawerOpen}
            onPlayerClick={handlePlayerClick}
            fetchReservations={() =>
              fetchGamesWithReservationsForComplex(
                selectedComplex?.value.id,
                selectedStatus,
                pageLimit
              )
            }
          />
        </div>
      </div>

      {isSecondDrawerOpen && (
        <PlayerReservationDetailsModal
          close={closeSecondDrawer}
          player={currentPlayer}
          team={currentGame?.reservation.team}
        />
      )}
    </>
  );
};

export default ReservationPage;
